import { CSSProperties, useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { styled, CustomThemeOptions } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { isSameDay } from 'date-fns/isSameDay'
import { toast } from 'react-toastify'

import {
  useSubEventsStatusTypeOptions,
  useSubEventsTypeOptions,
  useTagsDataOptions,
  useLocationDataOptions,
  useRoomsDataOptions,
  getOption,
  formatDateTime,
  formatHumanDateTime,
  formatHumanTime,
  searchString,
} from '../../../../../utils'
import { useDeleteSubEventMutation } from '../../../../../services/events'
import ContributorsAutocomplete from '../../../../root/components/ContributorsAutocomplete'
import {
  ModifySubEventsSchema,
  SessionSchema,
  SubEventSchema,
} from '../../../../events/Events.schema'
import { computeSubEventId, useAcceptedKeysPerSubEventType } from './SubEvents.utils'
import { Divider } from '@mui/material'
import { StatusTag } from '../../../../root/components/StatusTag'

const Span = styled('span')<{ lineHeight?: number; bold?: boolean }>(
  ({ theme, lineHeight, bold }) => ({
    color: theme.palette.text.secondary,
    lineHeight: lineHeight ?? undefined,
    fontWeight: bold ? 'bold' : 'normal',
  })
)

interface SubEventFormProps {
  id: number
  subEventKey: number
  minDateTime: Date | undefined
  maxDateTime: Date | undefined
  onRemove: () => void
  onDuplicate?: (data: SubEventSchema) => void
  onUpdateSession: (
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<SessionSchema> | undefined
  ) => void
  showSubEvents?: boolean
  width?:
    | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', CSSProperties['width']>>
    | CSSProperties['width']
  backgroundColor?: keyof CustomThemeOptions['colors']
  spacing?: number
  style?: React.CSSProperties
  location: number | undefined | null
}

export default function SubEventForm({
  subEventKey,
  minDateTime,
  maxDateTime,
  id,
  onRemove,
  onDuplicate,
  onUpdateSession,
  showSubEvents = true,
  width = '100%',
  backgroundColor = 'transparent',
  spacing = 2,
  style,
  location,
}: SubEventFormProps) {
  const { t } = useTranslation('events')
  const { eventId } = useParams()
  const subEventsTypeOptions = useSubEventsTypeOptions(t)
  const statusOptions = useSubEventsStatusTypeOptions(t)
  const tagsOptions = useTagsDataOptions()
  const locationsOptions = useLocationDataOptions()
  const roomsOptions = useRoomsDataOptions(location, locationsOptions)

  const [deleteSubEvent] = useDeleteSubEventMutation()

  const { control, watch, getValues, setValue } = useFormContext<ModifySubEventsSchema>()

  const sessions = watch(`sessions`)
  const sessionsId = watch(`subEvents.${subEventKey}.sessions`)
  const subEventType = watch(`subEvents.${subEventKey}.sub_event_type`)
  const name = watch(`subEvents.${subEventKey}.name`)
  const start = watch(`subEvents.${subEventKey}.start`)
  const end = watch(`subEvents.${subEventKey}.end`)

  const acceptedKeys = useAcceptedKeysPerSubEventType(subEventType)

  const [selectedSessions, setSelectedSessions] = useState(
    sessions.filter(session => sessionsId?.includes(session.id))
  )
  useEffect(() => {
    setSelectedSessions(sessions.filter(session => sessionsId?.includes(session.id)))
  }, [sessionsId, sessions])

  const innerMaxDateTime = useMemo(() => {
    if (selectedSessions && maxDateTime) {
      const res = selectedSessions.reduce((acc, session) => {
        if (new Date(session.end) < acc) {
          return new Date(session.end)
        }
        return acc
      }, maxDateTime)
      return res
    }
    return maxDateTime
  }, [maxDateTime, selectedSessions])
  const innerMinDateTime = useMemo(() => {
    if (selectedSessions && minDateTime) {
      const res = selectedSessions.reduce((acc, session) => {
        if (new Date(session.start) > acc) {
          return new Date(session.start)
        }
        return acc
      }, minDateTime)
      return res
    }
    return minDateTime
  }, [minDateTime, selectedSessions])

  return showSubEvents ? (
    <Stack
      spacing={spacing}
      padding={2}
      sx={theme => ({
        backgroundColor: theme.colors[backgroundColor],
        borderRadius: '1rem',
      })}
      width={width}
      margin="0 auto 0.6rem"
      id={computeSubEventId(id)}
      style={style}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="h5"
          margin={'0.5rem 0'}
          sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {t('Intervention')}
          <StatusTag
            color="#D9D9D9"
            size="small"
            position="top"
            absolute={false}
            sx={{ whiteSpace: 'nowrap' }}
          >
            ID {id}
          </StatusTag>
          {name} - {formatDateTime(start)} - {formatDateTime(end)}
        </Typography>
        {/* <Span bold>
          {t('Intervention')} {subEventKey + 1} : {name} - {formatDateTime(start)} -{' '}
          {formatDateTime(end)}
        </Span> */}
      </Stack>
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Span>{t("Détails de l'intervention")}</Span>
        <Stack direction="row" spacing={1}>
          <Controller
            control={control}
            name={`subEvents.${subEventKey}.hide_from_generic_program`}
            render={({ field, fieldState }) => (
              <FormGroup>
                <FormControlLabel
                  {...field}
                  control={<Switch defaultChecked={field.value} />}
                  label={t('Masquer du programme')}
                  sx={{ width: 'max-content' }}
                />
                {fieldState.error && (
                  <Typography color="error">{fieldState.error.message}</Typography>
                )}
              </FormGroup>
            )}
          />
          {onDuplicate && (
            <IconButton
              onClick={event => {
                event.preventDefault()
                const data = getValues(`subEvents.${subEventKey}`)
                onDuplicate?.(data)
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{ height: 'auto' }}
            onClick={event => {
              event.preventDefault()
              if (
                window.confirm(
                  t(
                    'Êtes-vous sûr de vouloir supprimer cette intervention ? La suppression sera immédiate en base de données mais les séances rattachées ne seront pas supprimées'
                  ) + '.'
                )
              ) {
                const id = getValues(`subEvents.${subEventKey}.id`)
                const isNew = getValues(`subEvents.${subEventKey}._new`)
                if (!isNew) {
                  deleteSubEvent({ eventId: Number(eventId), id: id })
                    .then(data => {
                      if ('data' in data) {
                        toast.success(t('Intervention supprimée'))
                      }
                      onRemove()
                    })
                    .catch(error => {
                      console.error(error)
                      toast.error(t('Une erreur est survenue'))
                    })
                } else {
                  onRemove()
                  toast.success(t('Intervention supprimée'))
                }
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={spacing}>
        <Controller
          control={control}
          name={`subEvents.${subEventKey}.sub_event_type`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? '')}
              onBlur={field.onBlur}
              defaultValue={getOption(subEventsTypeOptions, null, field.value)}
              options={subEventsTypeOptions}
              id="subEvent-type-autocomplete"
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  label={t("Type d'intervention")}
                  disabled={field.disabled}
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              sx={{
                display: !acceptedKeys.includes('sub_event_type') ? 'none' : undefined,
              }}
            />
          )}
        />
        <Controller
          control={control}
          name={`subEvents.${subEventKey}.status`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? '')}
              onBlur={field.onBlur}
              defaultValue={getOption(statusOptions, null, field.value) ?? statusOptions[0]}
              options={statusOptions}
              id="status-autocomplete"
              disablePortal
              fullWidth
              disableClearable
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  disabled={field.disabled}
                  label={t('Statut')}
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              sx={{
                display: !acceptedKeys.includes('status') ? 'none' : undefined,
              }}
            />
          )}
        />
      </Stack>
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.name`}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t("Nom de l'intervention")}
            fullWidth
            required
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              display: !acceptedKeys.includes('name') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.sessions`}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value, reason, details) => {
              const ids = value.map(option => option.id)
              field.onChange(ids)
              setSelectedSessions(value)
              onUpdateSession(reason, details)
            }}
            multiple
            value={selectedSessions}
            onBlur={field.onBlur}
            options={sessions}
            disableClearable
            id="sessionid-autocomplete"
            disablePortal
            fullWidth
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={x => x}
            getOptionLabel={option => {
              const sameDay = isSameDay(option.start, option.end)
              return `${formatHumanDateTime(option.start, true)}-${sameDay ? formatHumanTime(option.end) : formatHumanDateTime(option.end, true)} : ${option.name}`
            }}
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Séance(s) rattachée(s)')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={spacing}>
        <Controller
          control={control}
          name={`subEvents.${subEventKey}.start`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <DateTimePicker
              label={t('Date de début')}
              name={field.name}
              minDateTime={innerMinDateTime}
              maxDateTime={innerMaxDateTime}
              defaultValue={field.value ? new Date(field.value) : null}
              onChange={date => {
                field.onChange(date?.toISOString() ?? '')
              }}
              slotProps={{
                textField: {
                  required: true,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
              sx={{
                flex: '1 0 auto',
                display: !acceptedKeys.includes('start') ? 'none' : undefined,
              }}
            />
          )}
        />
        <Controller
          control={control}
          name={`subEvents.${subEventKey}.end`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <DateTimePicker
              label={t('Date de fin')}
              name={field.name}
              minDateTime={innerMinDateTime}
              maxDateTime={innerMaxDateTime}
              defaultValue={field.value ? new Date(field.value) : null}
              onChange={date => {
                field.onChange(date?.toISOString() ?? '')
              }}
              slotProps={{
                textField: {
                  required: true,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
              sx={{
                flex: '1 0 auto',
                display: !acceptedKeys.includes('end') ? 'none' : undefined,
              }}
            />
          )}
        />
      </Stack>
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.tags`}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => {
              const parsedValue = value.reduce((acc, option) => {
                if (option) acc.push(option.value)
                return acc
              }, [] as number[])
              field.onChange(parsedValue ?? '')
            }}
            onBlur={field.onBlur}
            defaultValue={
              field.value ? field.value.map(tagId => getOption(tagsOptions, null, tagId)) : []
            }
            options={tagsOptions}
            multiple
            id="tags-autocomplete"
            disablePortal
            disableClearable
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                label={t('Tags')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
            sx={{
              display: !acceptedKeys.includes('tags') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.location`}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => field.onChange(value?.value ?? null)}
            onBlur={field.onBlur}
            defaultValue={getOption(locationsOptions, null, field.value)}
            options={locationsOptions}
            id="location-autocomplete"
            disablePortal
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Lieu')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
            sx={{
              display: !acceptedKeys.includes('location') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.rooms`}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => {
              const parsedValue = value.reduce((acc, option) => {
                if (option) acc.push(option.value)
                return acc
              }, [] as number[])
              field.onChange(parsedValue)
            }}
            onBlur={field.onBlur}
            defaultValue={field.value
              .map(id => getOption(roomsOptions, null, id))
              .filter(el => !!el)}
            options={roomsOptions}
            // Filter options when the user types. It should look for the rooms and the locations (label and group field)
            filterOptions={(options, state) =>
              options.filter(
                option =>
                  searchString(option.label, state.inputValue) ||
                  searchString(option.group, state.inputValue)
              )
            }
            getOptionLabel={option =>
              option.groupId !== location ? `[${option.group}] ${option.label}` : option.label
            }
            id="rooms-autocomplete"
            groupBy={option => option.group}
            multiple
            disablePortal
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                label={t('Salle')}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
            sx={{
              display: !acceptedKeys.includes('rooms') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.description`}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Description')}
            fullWidth
            multiline
            minRows={2}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              display: !acceptedKeys.includes('description') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.comment`}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Commentaire')}
            fullWidth
            multiline
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              display: !acceptedKeys.includes('comment') ? 'none' : undefined,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`subEvents.${subEventKey}.contributors`}
        render={({ field, fieldState }) => (
          <ContributorsAutocomplete
            prependName={field.name}
            defaultValue={field.value}
            onChange={field.onChange}
            label={<Span lineHeight={1}>{t('Contributeurs')}</Span>}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              display: !acceptedKeys.includes('contributors') ? 'none' : undefined,
            }}
          />
        )}
      />
    </Stack>
  ) : (
    // Fix: force minHeight to avoid zero-sized list potential error from Virtuoso
    // TODO Filter out empty subEvents - but may force rerender
    <div style={{ minHeight: '1px' }} />
  )
}
